<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { NLayoutSider, NMenu } from 'naive-ui'
import FormCommon from '../../../views/folder/FormCommon.vue'
import { useNavigation } from './composables/useNavigation'
import { useSiderMenu } from './composables/useSiderMenu'
import { PromptStore } from '@/components/common'
import { useAppStore, useAuthStore, useUserStore } from '@/store'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { t } from '@/locales'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import { useTheme } from '@/hooks/useTheme'
import logoDark from '@/assets/login/logoLoginDark.png'
import logoLight from '@/assets/login/logoLoginLight.png'
import logoCollapsed from '@/assets/login/logoIcon.png'

const { isMobile } = useBasicLayout()
const appStore = useAppStore()
const userStore = useUserStore()
const workspaceFolderStore = useWorkspaceFolderStore()
const { isDark } = useTheme()
const authStore = useAuthStore()
const {
  collapsed,
  selectedKey,
  safeAreaStyle,
  getMobileClass,
  handleUpdateCollapsed,
  handleSelect,
} = useNavigation()

const showPrompt = ref(false)
const showForm = ref(false)

function handleAdd() {
  showForm.value = true
}

const onCloseForm = (reload: boolean) => {
  showForm.value = false
  if (reload)
    workspaceFolderStore.fetchFolders(workSpaceId.value)
}

const { listMenu, expandedKeys } = useSiderMenu(collapsed, handleAdd)

const workSpaceId = computed(() => userStore.workSpaceId)

watch(isMobile, val => appStore.setSiderCollapsed(val), { immediate: true, flush: 'post' })

onMounted(async () => {
  if (authStore?.token) {
    await workspaceFolderStore.fetchFolders(workSpaceId.value)
    await userStore.fetchUserRoleInfo()
  }
})
</script>

<template>
  <NLayoutSider
    :collapsed="collapsed"
    :collapsed-width="70"
    :width="260"
    :show-trigger="!isMobile && 'arrow-circle'"
    collapse-mode="width"
    position="absolute"
    bordered
    class="transition-all duration-300 ease-in-out shadow-lg"
    :style="getMobileClass"
    @update-collapsed="handleUpdateCollapsed"
  >
    <div class="flex flex-col h-full bg-white dark:bg-[#1e1e2d]" :style="safeAreaStyle">
      <!-- Header with Logo -->
      <header class="flex items-center" :class="{ 'justify-center px-2': collapsed, 'px-6': !collapsed }">
        <RouterLink to="/agent-management" class="flex items-center py-4">
          <img
            v-if="!collapsed"
            :src="isDark ? logoDark : logoLight"
            alt="Logo"
          >
          <img
            v-else
            :src="logoCollapsed"
            class="w-10"
            alt="Logo Collapsed"
          >
        </RouterLink>
      </header>

      <!-- Main Content -->
      <main class="flex-1 overflow-hidden flex flex-col">
        <!-- Navigation Menu -->
        <nav class="flex-1 overflow-auto px-2">
          <NMenu
            :options="listMenu"
            :collapsed="collapsed"
            :collapsed-width="52"
            :value="selectedKey"
            :indent="16"
            :icon-size="20"
            :collapsed-icon-size="20"
            :default-expanded-keys="expandedKeys"
            class="custom-menu"
            @update:value="handleSelect"
          />
        </nav>
      </main>
    </div>
  </NLayoutSider>

  <!-- Mobile Overlay -->
  <div
    v-if="isMobile && !collapsed"
    class="fixed inset-0 z-40 bg-black/40"
    @click="handleUpdateCollapsed"
  />

  <!-- Modals -->
  <PromptStore v-model:visible="showPrompt" />
  <FormCommon
    v-if="showForm"
    :data-source="{}"
    :show="showForm"
    :is-view="false"
    :is-edit="false"
    :is-create="true"
    :title="t('folder.create')"
    @on-close="onCloseForm"
  />
</template>

<style scoped>
:deep(.n-menu-item-content) {
  padding-right: 16px;
  font-weight: 400;
  font-size: 14px;
  opacity: 0.9;
  color: #666666 !important;
}

:deep(.popover-menu-wrapper .n-menu) {
  background: transparent;
}

:deep(.popover-menu-wrapper .n-menu-item) {
  padding: 8px 12px;
  margin: 2px 0;
  border-radius: 6px;
  transition: all 0.2s ease;
}

:deep(.popover-menu-wrapper .n-menu-item:hover) {
  background-color: #f5f5f5;
  transform: translateY(-1px);
}

:deep(.tool-group-title) {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding: 4px 8px;
}
:deep(.custom-menu.n-menu .n-menu-item) {
	height: 35px;
	/* //margin-top: 0; */
}

:deep(.custom-menu.n-menu .n-submenu .n-menu-item-content) {
	height: 35px;
}

/* Active styles
   - Parent submenu: highlight background when itself selected OR when any child is active
   - Child items: only change text color on active, no active background */
/* Base selected item background (parent items) */
:deep(.custom-menu.n-menu .n-menu-item-content.n-menu-item-content--selected)::before {
  background-color: #F3E8FF !important; /* purple-100 */
}
:deep(.custom-menu.n-menu .n-menu-item-content.n-menu-item-content--selected) {
  color: #5B21B6 !important;  /* purple-800 */
  border-radius: 8px;
}
/* When a child is active, mark the parent title as active with background */
:deep(.custom-menu.n-menu .n-submenu.n-submenu--child-active > .n-menu-item-content)::before,
:deep(.custom-menu.n-menu .n-submenu.n-submenu--active > .n-menu-item-content)::before {
  background-color: #F3E8FF !important;
}
:deep(.custom-menu.n-menu .n-submenu.n-submenu--child-active > .n-menu-item-content),
:deep(.custom-menu.n-menu .n-submenu.n-submenu--active > .n-menu-item-content) {
  color: #5B21B6 !important;
  border-radius: 8px;
}
/* Child selected: remove active background, keep active color only */
:deep(.custom-menu.n-menu .n-submenu-children .n-menu-item-content.n-menu-item-content--selected)::before {
  background-color: transparent !important;
}
:deep(.custom-menu.n-menu .n-submenu-children .n-menu-item-content.n-menu-item-content--selected) {
  color: #5B21B6 !important;
}
/* Ensure ALL child items never show background highlight (hover/active/selected) */
:deep(.custom-menu.n-menu .n-submenu-children .n-menu-item-content::before) {
  background-color: transparent !important;
}
:deep(.custom-menu.n-menu .n-submenu-children .n-menu-item-content:hover) {
  color: #5B21B6; /* optional hover color */
}

:deep(.n-submenu-children) {
	position: relative;
}
:deep(.n-submenu-children::before) {
	content: '';
	position: absolute;
	left: 25px; /* align with deeper indent */
	top: 6px;
	bottom: 6px;
	width: 1px;
	background-color: rgba(0, 0, 0, 0.15);
}
:deep(.n-submenu-children .n-menu-item-content) {
	/* deeper indent for submenu items */
	padding-left: 43px !important;
}
</style>
